import { Notification } from "components";
import { EnumEmployeeDelegationMode } from "constants/enums/employee-delegation-mode";
import { compose, withFormik, withHooks, withTranslation } from "enhancers";
import withPreventLeaveDirtyForm from "enhancers/withPreventLeaveDirtyForm";
import { PageContent } from "layouts";
import { isEmpty, map } from "lodash";
import { gql, homePath, paths } from "utils/helper";
import { EMPLOYEE_DELEGATION_API } from "..";
import { EmployeeDelegationForm } from "../components/EmployeeDelegationForm";
import { validationSchema } from "../create";

const API = {
  GET_EMPLOYEE_DELEGATION: gql`
    query GET_EMPLOYEE_DELEGATION($id: String!) {
      employeeDelegation(id: $id) {
        id
        delegate {
          id
          firstName
          lastName
          employeeCode
        }
        mode
        targets {
          id
          firstName
          lastName
          employeeCode
        }
      }
    }
  `,
  UPDATE_EMPLOYEE_DELEGATION: gql`
    mutation UPDATE_EMPLOYEE_DELEGATION(
      $id: String!
      $delegateId: String!
      $mode: String!
      $targets: [String!]
    ) {
      updateEmployeeDelegation(
        input: {
          id: $id
          delegateId: $delegateId
          mode: $mode
          targets: $targets
        }
      ) {
        id
      }
    }
  `,
};

const EmployeeDelegationDetailPage = ({
  title,
  breadcrumbs,
  loading,
  isAllMode,
  isSubmitting,
  selectedDelegateId,
}: any) => {
  return (
    <PageContent title={title} breadcrumbs={breadcrumbs} loading={loading}>
      <EmployeeDelegationForm
        isAllMode={isAllMode}
        isSubmitting={isSubmitting}
        selectedDelegateId={selectedDelegateId}
      />
    </PageContent>
  );
};

const enhancer = compose(
  withFormik({
    validationSchema,
  }),
  withPreventLeaveDirtyForm(),
  withTranslation({ prefix: "pages.main.employeeDelegation.edit" }),
  withHooks((props: any, hooks: any) => {
    const {
      t,
      values,
      setInitialValues,
      initialValues,
      isSubmitting,
      setFieldValue,
    } = props;
    const {
      useMemo,
      useHandleSubmit,
      useQuery,
      useMutation,
      useEffect,
      useState,
      useParams,
    } = hooks;

    const { id } = useParams();
    const { data, loading } = useQuery(API.GET_EMPLOYEE_DELEGATION, {
      fetchPolicy: "network-only",
      variables: { id },
      onError: () => {
        paths.employeeDelegationPath().push();
      },
    });

    const [updateEmployeeDelegation] = useMutation(
      API.UPDATE_EMPLOYEE_DELEGATION,
      {
        refetchQueries: [
          {
            query: EMPLOYEE_DELEGATION_API.FETCH_EMPLOYEE_DELEGATIONS,
          },
          {
            query: API.GET_EMPLOYEE_DELEGATION,
            variables: { id },
          },
        ],
        onCompleted: () => {
          Notification.success(t(".success"));
          paths.employeeDelegationPath().push();
        },
      }
    );

    const title = t(".title");

    const breadcrumbs = useMemo(
      () => [
        { path: homePath(), label: t(".home") },
        {
          path: paths.employeeDelegationPath(),
          label: t(".employeeDelegation"),
        },
        { path: null, label: title },
      ],
      [title, t]
    );

    useHandleSubmit((values: any) => {
      updateEmployeeDelegation(
        {
          variables: {
            id,
            delegateId: values.delegateId,
            mode: values.mode,
            targets: values.targets,
          },
        },
        {
          onCompleted: () => {
            Notification.success(t(".success"));
            paths.employeeDelegationPath().push();
          },
        }
      );
    });

    const isAllMode = useMemo(() => {
      return values?.mode === EnumEmployeeDelegationMode.ALL;
    }, [values]);

    useEffect(() => {
      if (isAllMode && values.targets?.length > 0) {
        setFieldValue("targets", []);
      }
    }, [values, setFieldValue, isAllMode]);

    const [initialized, setInitialized] = useState(false);
    useEffect(() => {
      if (isEmpty(initialValues) && data?.employeeDelegation) {
        setInitialValues({
          delegateId: data.employeeDelegation.delegate.id,
          mode: data.employeeDelegation.mode,
          targets: map(data.employeeDelegation.targets, "id"),
        });
      }
    }, [data, initialValues, setInitialValues]);

    useEffect(() => {
      if (!isEmpty(initialValues) && !initialized && !isEmpty(values)) {
        setInitialized(true);
      }
    }, [initialValues, values, initialized]);

    const isLoading = useMemo(() => {
      return loading || !initialized;
    }, [loading, initialized]);

    const selectedDelegateId = useMemo(() => {
      return values?.delegateId;
    }, [values?.delegateId]);

    return {
      title,
      breadcrumbs,
      isAllMode,
      isSubmitting,
      loading: isLoading,
      selectedDelegateId,
    };
  })
);

export default enhancer(EmployeeDelegationDetailPage);
